import NewsletterFormFields, {
  useNewsletterForm,
  type NewsletterFormClasses,
} from "./newsletterFormInner";

interface EmailCaptureFormProps {
  /** Custom call-to-action text above the form (hero / inline only). */
  cta?: string;
  /** Layout variant. */
  variant?: "hero" | "sidebar" | "inline" | "footer";
  /** Custom button text. Defaults differ per variant. */
  buttonText?: string;
}

// Shared snippets for compact variants (sidebar + footer).
const COMPACT_INPUT_BASE =
  "w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 transition-colors focus:ring-2 focus:outline-none";
const COMPACT_INPUT_ERROR = "border-red-400 focus:ring-red-200";
const COMPACT_BUTTON =
  "w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:outline-none disabled:opacity-50";

const HERO_CLASSES: NewsletterFormClasses = {
  form: "flex flex-col items-center justify-center gap-3 sm:flex-row",
  input:
    "w-full flex-grow rounded-lg border border-gray-200 bg-white px-4 py-3 text-base text-gray-900 shadow-sm transition-all duration-150 focus:ring-2 focus:outline-none sm:w-3/4",
  inputError: "ring-2 ring-red-400 focus:ring-red-300",
  inputDefault: "focus:border-blue-300 focus:ring-blue-200",
  button:
    "w-full rounded-lg bg-blue-600 px-6 py-3 text-base font-semibold text-white shadow-sm transition-all duration-150 hover:bg-blue-700 hover:shadow-md focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:outline-none disabled:opacity-50 sm:w-auto sm:whitespace-nowrap",
  error: "mt-2 text-center text-sm text-red-600",
};

const INLINE_CLASSES: NewsletterFormClasses = {
  form: "mx-auto flex max-w-xl flex-col items-center gap-3 sm:flex-row",
  input:
    "w-full flex-grow rounded-lg border bg-white px-4 py-3 text-base text-gray-900 transition-colors focus:ring-2 focus:outline-none",
  inputError: "border-red-400 focus:ring-red-200",
  inputDefault: "border-gray-300 focus:ring-orange-200",
  button:
    "w-full shrink-0 rounded-lg bg-blue-600 px-8 py-3 text-base font-semibold text-white transition-colors hover:bg-blue-700 focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:outline-none disabled:opacity-50 sm:w-auto",
  error: "mt-3 text-sm text-red-600",
};

const SIDEBAR_CLASSES: NewsletterFormClasses = {
  form: "flex flex-col gap-2",
  input: COMPACT_INPUT_BASE,
  inputError: COMPACT_INPUT_ERROR,
  inputDefault: "border-gray-300 focus:ring-orange-200",
  button: COMPACT_BUTTON,
  error: "mt-1 text-xs text-red-600",
  errorInline: true,
};

const FOOTER_CLASSES: NewsletterFormClasses = {
  form: "flex flex-col gap-2",
  input: COMPACT_INPUT_BASE,
  inputError: COMPACT_INPUT_ERROR,
  inputDefault: "border-gray-300 focus:ring-blue-300",
  button: COMPACT_BUTTON,
  error: "mt-1 text-xs text-red-300",
  errorInline: true,
};

function HeroVariant({ cta, buttonText }: EmailCaptureFormProps) {
  const form = useNewsletterForm("hero");

  if (form.submitted) {
    return (
      <div className="mx-auto my-10 w-full max-w-4xl px-4">
        <div className="mx-auto max-w-md rounded-lg border border-green-200 bg-green-50 px-6 py-5 text-center">
          <p className="font-semibold text-green-800">You're almost there!</p>
          <p className="mt-1 text-sm text-green-700">
            Click the link in the confirmation email from{" "}
            <span className="font-medium">newsletter@datafirstjobs.com</span> to
            activate your subscription.
          </p>
          <p className="mt-2 text-xs text-green-700/80">
            Don't see it within a minute? Check your spam or promotions folder.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto my-10 w-full max-w-4xl px-4">
      {cta && <p className="mb-8 text-center text-xl md:text-2xl">{cta}</p>}
      <div className="mx-auto w-full max-w-xl">
        <NewsletterFormFields
          form={form}
          classes={HERO_CLASSES}
          placeholder="Enter your email"
          buttonText={buttonText ?? "Get Jobs Twice a Week"}
        />
        <p className="mt-3 text-center text-sm text-gray-400">
          Free, no spam. Unsubscribe anytime.
        </p>
      </div>
    </div>
  );
}

function InlineVariant({ cta, buttonText }: EmailCaptureFormProps) {
  const form = useNewsletterForm("inline");

  if (form.submitted) {
    return (
      <div className="mt-8 rounded-lg border border-green-200 bg-green-50 px-6 py-6 text-center">
        <p className="text-sm font-semibold text-green-800">
          You're almost there!
        </p>
        <p className="mt-1 text-sm text-green-700">
          Click the link in the confirmation email from{" "}
          <span className="font-medium">newsletter@datafirstjobs.com</span> to
          activate your subscription.
        </p>
        <p className="mt-2 text-xs text-green-700/80">
          Don't see it within a minute? Check your spam or promotions folder.
        </p>
      </div>
    );
  }

  return (
    <div className="border-t border-orange-100 bg-orange-50 px-6 py-16 text-center sm:py-20">
      {cta && (
        <p className="mx-auto mb-6 max-w-xl text-xl font-semibold tracking-tight text-gray-800 md:text-2xl">
          {cta}
        </p>
      )}
      <NewsletterFormFields
        form={form}
        classes={INLINE_CLASSES}
        placeholder="Your email address"
        buttonText={buttonText ?? "Subscribe"}
      />
      <p className="mt-4 text-sm text-gray-500">
        Free, no spam. Unsubscribe anytime.
      </p>
    </div>
  );
}

function SidebarVariant({ buttonText }: EmailCaptureFormProps) {
  const form = useNewsletterForm("sidebar");

  if (form.submitted) {
    return (
      <div className="mt-3 rounded-lg border border-green-200 bg-green-50 px-5 py-5">
        <p className="text-sm font-semibold text-green-800">
          You're almost there!
        </p>
        <p className="mt-1 text-sm text-green-700">
          Click the link from{" "}
          <span className="font-medium">newsletter@datafirstjobs.com</span> to
          confirm.
        </p>
        <p className="mt-2 text-xs text-green-700/80">
          Check spam if you don't see it.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-lg border border-orange-100 bg-orange-50 px-5 py-5">
      <p className="text-polaris-navy mb-1 text-sm font-semibold">
        Don't miss new listings
      </p>
      <p className="mb-3 text-xs text-gray-500">
        Carefully selected data jobs, twice a week.
      </p>
      <NewsletterFormFields
        form={form}
        classes={SIDEBAR_CLASSES}
        placeholder="Your email address"
        buttonText={buttonText ?? "Subscribe"}
      />
    </div>
  );
}

function FooterVariant({ buttonText }: EmailCaptureFormProps) {
  const form = useNewsletterForm("footer");

  if (form.submitted) {
    return (
      <div className="rounded-md px-1 py-3">
        <p className="text-sm font-semibold text-green-200">
          You're almost there!
        </p>
        <p className="mt-1 text-sm text-green-300">
          Click the link from{" "}
          <span className="font-medium">newsletter@datafirstjobs.com</span> to
          confirm.
        </p>
        <p className="mt-2 text-xs text-green-300/80">
          Check spam if you don't see it.
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="mb-1 text-sm font-semibold text-white">
        Don't miss new listings
      </p>
      <p className="mb-3 text-xs text-blue-300">
        Carefully selected data jobs, twice a week.
      </p>
      <NewsletterFormFields
        form={form}
        classes={FOOTER_CLASSES}
        placeholder="Your email address"
        buttonText={buttonText ?? "Subscribe"}
      />
    </div>
  );
}

export default function EmailCaptureForm({
  cta,
  variant = "hero",
  buttonText,
}: EmailCaptureFormProps) {
  if (variant === "sidebar") return <SidebarVariant buttonText={buttonText} />;
  if (variant === "footer") return <FooterVariant buttonText={buttonText} />;
  if (variant === "inline")
    return <InlineVariant cta={cta} buttonText={buttonText} />;
  return <HeroVariant cta={cta} buttonText={buttonText} />;
}
